import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';

const AdminClientSetup = () => {
    // 🗄️ GLOBAL CLIENTS LIST (For Dropdowns)
    const [clientsList, setClientsList] = useState([]);
    const [isLoadingClients, setIsLoadingClients] = useState(true);

    // 🔗 API LINK MANAGER
    const [clientId, setClientId] = useState('');
    const [whatsappId, setWhatsappId] = useState('');
    const [metaToken, setMetaToken] = useState('');
    const [status, setStatus] = useState('');

    // 🎓 COURSES MANAGER
    const [courseClientId, setCourseClientId] = useState('');
    const [courses, setCourses] = useState([]);
    const [newCourse, setNewCourse] = useState('');
    const [courseStatus, setCourseStatus] = useState('');
    const [isLoadingCourses, setIsLoadingCourses] = useState(false);
    const [isSavingCourses, setIsSavingCourses] = useState(false);

    // 👑 GOD MODE: PLAN MANAGER
    const [planClientId, setPlanClientId] = useState('');
    const [planType, setPlanType] = useState('Growth Plan');
    const [planDuration, setPlanDuration] = useState('12');
    const [planStatus, setPlanStatus] = useState('');
    const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);

    // ==========================================
    // AUTO-FETCH ALL CLIENTS ON LOAD
    // ==========================================
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "clients"));
                const clientsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || "Unknown Business",
                    phone: doc.data().coordinator || doc.id.replace('client_', '')
                }));
                setClientsList(clientsData);
            } catch (error) {
                console.error("Error fetching clients:", error);
            } finally {
                setIsLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    // ==========================================
    // HANDLERS: COURSES
    // ==========================================
    const handleLoadCourses = async () => {
        if (!courseClientId) return setCourseStatus('❌ Client select karo pehle.');
        setIsLoadingCourses(true);
        try {
            const snap = await getDoc(doc(db, 'clients', courseClientId));
            if (snap.exists()) {
                setCourses(Array.isArray(snap.data().courses) ? snap.data().courses : []);
                setCourseStatus('✅ Courses load ho gaye!');
            } else {
                setCourseStatus('❌ Client nahi mila.');
            }
        } catch(e) { setCourseStatus('❌ Error: ' + e.message); }
        setIsLoadingCourses(false);
    };

    const handleAddCourse = () => {
        const trimmed = newCourse.trim();
        if (!trimmed) return;
        if (trimmed.length > 24) return setCourseStatus('❌ Max 24 characters allowed.');
        if (courses.includes(trimmed)) return setCourseStatus('❌ Yeh course pehle se hai.');
        if (courses.length >= 9) return setCourseStatus('❌ Max 9 courses allowed.');
        setCourses([...courses, trimmed]);
        setNewCourse('');
        setCourseStatus('');
    };

    const handleSaveCourses = async () => {
        if (!courseClientId) return setCourseStatus('❌ Client select karo pehle.');
        setIsSavingCourses(true);
        try {
            await updateDoc(doc(db, 'clients', courseClientId), { courses });
            setCourseStatus('✅ Courses successfully save ho gaye!');
        } catch(e) { setCourseStatus('❌ Save failed: ' + e.message); }
        setIsSavingCourses(false);
    };

    // ==========================================
    // HANDLER: API SETUP
    // ==========================================
    const handleSetup = async (e) => {
        e.preventDefault();
        setStatus('Setting up... ⏳');

        try {
            const response = await fetch('https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/adminManualSetup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clientId, whatsappId, metaToken })
            });

            const data = await response.json();
            if (data.success) {
                setStatus('✅ Client Setup Done Successfully!');
                setClientId(''); setWhatsappId(''); setMetaToken('');
            } else {
                setStatus('❌ Error: ' + data.error);
            }
        } catch (error) {
            setStatus('❌ System Error: ' + error.message);
        }
    };

    // ==========================================
    // HANDLER: GOD MODE PLAN UPGRADE
    // ==========================================
    const handlePlanUpdate = async (e) => {
        e.preventDefault();
        if (!planClientId) return setPlanStatus('❌ Please select a client.');
        
        const selectedClientName = clientsList.find(c => c.id === planClientId)?.name || "this client";
        
        const isConfirmed = window.confirm(`👑 Wait! Kya aap sach mein "${selectedClientName}" ko "${planType}" grant karna chahte hain?`);
        if (!isConfirmed) return;

        setIsUpdatingPlan(true);
        setPlanStatus('Upgrading system... ⏳');

        try {
            const response = await fetch('https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/adminUpdatePlan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clientId: planClientId,
                    planType: planType,
                    durationMonths: parseInt(planDuration)
                })
            });

            const data = await response.json();
            if (data.success) {
                setPlanStatus(`✅ ${data.message}`);
                setPlanClientId(''); // reset input
            } else {
                setPlanStatus('❌ Error: ' + data.error);
            }
        } catch (error) {
            setPlanStatus('❌ System Error: ' + error.message);
        }
        setIsUpdatingPlan(false);
    };

    return (
        <div style={{ padding: '30px', maxWidth: '800px', margin: '0 auto', fontFamily: 'sans-serif' }}>
            <h1 style={{ color: '#25D366' }}>🚀 SaaS Admin: Command Center</h1>
            
            {/* 1. INTERNAL CHEAT SHEET */}
            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '30px', borderLeft: '5px solid #1877F2' }}>
                <h3>🛠️ Quick Setup Guide (DFY)</h3>
                <ol style={{ lineHeight: '1.8' }}>
                    <li>Go to <b>Meta Business Settings &gt; Users &gt; System Users</b>.</li>
                    <li>Create Admin User <b>SaaS_Bot_Admin</b> and click <b>Add Assets</b> (Select Client's App &gt; Full Control).</li>
                    <li>Click <b>Generate New Token</b> &rarr; Select <b>Never Expire</b>.</li>
                    <li>Check: <code>whatsapp_business_management</code> AND <code>whatsapp_business_messaging</code>.</li>
                    <li>Copy this Token. Then copy <b>Phone Number ID</b> from WhatsApp Manager.</li>
                </ol>
            </div>

            {/* 👑 GOD MODE: PLAN MANAGER */}
            <div style={{ background: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginBottom: '30px', borderTop: '5px solid #FFD700' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>👑 God Mode: Manual Plan Upgrade</h3>
                <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>Bypass the trial period and instantly grant Premium/Paid access to any client.</p>

                <form onSubmit={handlePlanUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                        <label><b>Select Client:</b></label>
                        <select 
                            value={planClientId} 
                            onChange={(e) => setPlanClientId(e.target.value)} 
                            style={{ width: '100%', padding: '10px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fdfdfd' }} 
                            required
                            disabled={isLoadingClients}
                        >
                            <option value="">{isLoadingClients ? "Loading clients..." : "-- Select a Client --"}</option>
                            {clientsList.map(c => (
                                <option key={c.id} value={c.id}>{c.name} (+{c.phone})</option>
                            ))}
                        </select>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ flex: 1 }}>
                            <label><b>Plan Type:</b></label>
                            <select 
                                value={planType} 
                                onChange={(e) => setPlanType(e.target.value)}
                                style={{ width: '100%', padding: '10px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '6px' }}
                            >
                                <option value="Starter Plan">Starter Plan</option>
                                <option value="Growth Plan">Growth Plan</option>
                                <option value="Full Suite Plan">Full Suite Plan</option>
                                <option value="Custom Enterprise">Custom Enterprise</option>
                            </select>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label><b>Duration:</b></label>
                            <select 
                                value={planDuration} 
                                onChange={(e) => setPlanDuration(e.target.value)}
                                style={{ width: '100%', padding: '10px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '6px' }}
                            >
                                <option value="1">1 Month</option>
                                <option value="3">3 Months (Quarterly)</option>
                                <option value="6">6 Months (Half-Yearly)</option>
                                <option value="12">12 Months (Yearly)</option>
                                <option value="120">10 Years (Lifetime)</option>
                            </select>
                        </div>
                    </div>

                    <button type="submit" disabled={isUpdatingPlan || !planClientId} style={{ background: '#FFD700', color: '#333', padding: '15px', border: 'none', borderRadius: '5px', fontSize: '16px', fontWeight: '900', cursor: 'pointer', marginTop: '10px', boxShadow: '0 4px 6px rgba(255, 215, 0, 0.3)' }}>
                        {isUpdatingPlan ? 'Upgrading System... ⏳' : '👑 Grant Premium Access'}
                    </button>
                </form>
                {planStatus && <p style={{ marginTop: '15px', fontWeight: 'bold', textAlign: 'center' }}>{planStatus}</p>}
            </div>

            {/* 2. SETUP FORM */}
            <div style={{ background: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3>🔗 Link Client Meta API</h3>
                <form onSubmit={handleSetup} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                        <label><b>Select Client:</b></label>
                        <select 
                            value={clientId} 
                            onChange={(e) => setClientId(e.target.value)} 
                            style={{ width: '100%', padding: '10px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fdfdfd' }} 
                            required
                            disabled={isLoadingClients}
                        >
                            <option value="">{isLoadingClients ? "Loading clients..." : "-- Select a Client --"}</option>
                            {clientsList.map(c => (
                                <option key={c.id} value={c.id}>{c.name} (+{c.phone})</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label><b>WhatsApp Phone Number ID:</b></label>
                        <input 
                            type="text" 
                            placeholder="e.g. 103077682678158" 
                            value={whatsappId} 
                            onChange={(e) => setWhatsappId(e.target.value)} 
                            style={{ width: '100%', padding: '10px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '6px' }} required 
                        />
                    </div>
                    <div>
                        <label><b>Permanent Meta Token:</b></label>
                        <textarea 
                            rows="3"
                            placeholder="Paste the long token starting with EAAL..." 
                            value={metaToken} 
                            onChange={(e) => setMetaToken(e.target.value)} 
                            style={{ width: '100%', padding: '10px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '6px' }} required 
                        />
                    </div>
                    <button type="submit" disabled={!clientId} style={{ background: '#25D366', color: 'white', padding: '15px', border: 'none', borderRadius: '5px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>
                        Save Client Configuration 💾
                    </button>
                </form>
                {status && <p style={{ marginTop: '20px', fontWeight: 'bold', textAlign: 'center' }}>{status}</p>}
            </div>

            {/* 🎓 COURSES MANAGER */}
            <div style={{ background: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '30px' }}>
                <h3>🎓 Bot Course List Manager</h3>
                <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>Kisi bhi client ke WhatsApp bot courses yahan se manage karein.</p>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                    <select
                        value={courseClientId}
                        onChange={(e) => {
                            setCourseClientId(e.target.value);
                            setCourses([]); // Dropdown change hone par purane courses hata do
                            setCourseStatus('');
                        }}
                        style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fdfdfd' }}
                        disabled={isLoadingClients}
                    >
                        <option value="">{isLoadingClients ? "Loading clients..." : "-- Select Client --"}</option>
                        {clientsList.map(c => (
                            <option key={c.id} value={c.id}>{c.name} (+{c.phone})</option>
                        ))}
                    </select>
                    <button onClick={handleLoadCourses} disabled={isLoadingCourses || !courseClientId} style={{ background: '#1877F2', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                        {isLoadingCourses ? 'Loading...' : 'Load'}
                    </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', minHeight: '40px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
                    {courses.map((course, i) => (
                        <span key={i} style={{ background: '#e8f0fe', color: '#1a73e8', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {course}
                            <button onClick={() => setCourses(courses.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', lineHeight: 1 }}>×</button>
                        </span>
                    ))}
                    {courses.length === 0 && <span style={{ color: '#aaa', fontSize: '13px' }}>Pehle client load karein ya course add karein</span>}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                    <input
                        type="text"
                        placeholder="Naya course likhein (max 24 chars)"
                        value={newCourse}
                        onChange={(e) => setNewCourse(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCourse()}
                        maxLength={24}
                        style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                        disabled={!courseClientId}
                    />
                    <button onClick={handleAddCourse} disabled={!courseClientId} style={{ background: '#25D366', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>+ Add</button>
                </div>
                <button onClick={handleSaveCourses} disabled={isSavingCourses || !courseClientId} style={{ background: '#333', color: 'white', padding: '12px', width: '100%', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {isSavingCourses ? 'Saving...' : '💾 Save Courses to Firestore'}
                </button>
                {courseStatus && <p style={{ marginTop: '12px', fontWeight: 'bold', textAlign: 'center' }}>{courseStatus}</p>}
            </div>
        </div>
    );
};

export default AdminClientSetup;